import React, { useState, useEffect, useCallback, useRef } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Trophy, Check, X } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const VOTE_VALUES = [3, 2, 1];
const VOTE_COLORS = {
  3: "bg-amber-500 text-white",
  2: "bg-slate-400 text-white",
  1: "bg-amber-700 text-white",
};
const VOTE_LABELS = { 3: "3 votes", 2: "2 votes", 1: "1 vote" };

export function BFVotingSection({ onPendingChange, returnToRef, openTrigger }) {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeGame, setActiveGame] = useState(null);
  const [players, setPlayers] = useState([]);
  const [playersLoading, setPlayersLoading] = useState(false);
  const [votes, setVotes] = useState({}); // { player_uid: pointValue }
  const [submitting, setSubmitting] = useState(false);
  const sectionRef = useRef(null);

  const fetchPending = useCallback(async () => {
    try {
      const { data } = await api.get("/bf-votes/my-pending");
      const list = data.pending || [];
      setPending(list);
      if (onPendingChange) onPendingChange(list.length);
    } catch {
      // Not logged in or no pending
      if (onPendingChange) onPendingChange(0);
    }
    setLoading(false);
  }, [onPendingChange]);

  useEffect(() => { fetchPending(); }, [fetchPending]);

  // When openTrigger increments, auto-open the first pending vote dialog
  useEffect(() => {
    if (openTrigger && pending.length > 0) {
      openVoting(pending[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openTrigger]);


  const openVoting = async (game) => {
    setActiveGame(game);
    setVotes({});
    setPlayersLoading(true);
    try {
      const { data } = await api.get(`/bf-votes/${game.game_id}/players`);
      setPlayers(data.players || []);
    } catch {
      toast.error("Failed to load players");
      setPlayers([]);
    }
    setPlayersLoading(false);
  };

  const assignVote = (playerId, value) => {
    setVotes((prev) => {
      const next = { ...prev };
      // Remove this value from whoever had it
      for (const [k, v] of Object.entries(next)) {
        if (v === value) delete next[k];
      }
      // Remove any existing vote for this player
      delete next[playerId];
      // Assign
      next[playerId] = value;
      return next;
    });
  };

  const removeVote = (playerId) => {
    setVotes((prev) => {
      const next = { ...prev };
      delete next[playerId];
      return next;
    });
  };

  const submitVotes = async () => {
    if (Object.keys(votes).length !== 3) {
      toast.error("Assign all three votes (3, 2, 1) before saving");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/bf-votes/${activeGame.game_id}`, { votes });
      toast.success("B&F votes saved!");
      setActiveGame(null);
      // Remove from pending
      setPending((prev) => {
        const next = prev.filter((p) => p.game_id !== activeGame.game_id);
        if (onPendingChange) onPendingChange(next.length);
        // Scroll back to availability section if all votes done
        if (next.length === 0 && returnToRef?.current) {
          setTimeout(() => {
            returnToRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
          }, 400);
        }
        return next;
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save votes");
    }
    setSubmitting(false);
  };

  if (loading || pending.length === 0) return null;

  const usedValues = new Set(Object.values(votes));
  const availableValues = VOTE_VALUES.filter((v) => !usedValues.has(v));

  return (
    <>
      <section data-testid="bf-voting-section">
        <div className="space-y-2">
          {pending.map((g) => (
            <div
              key={g.game_id}
              className="flex items-center gap-3 px-4 py-3 rounded-xl border border-amber-500/30 bg-amber-500/5 cursor-pointer hover:bg-amber-500/10 transition-colors"
              onClick={() => openVoting(g)}
              data-testid={`bf-vote-card-${g.game_id}`}
            >
              <Trophy className="h-4 w-4 text-amber-500 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">
                  B&F Vote: {g.grade || g.club_team_name || "Match"}{g.round ? ` — ${g.round}` : ""}
                </div>
              </div>
              <Badge variant="outline" className="text-[10px] font-semibold bg-amber-500/10 text-amber-600 border-amber-500/30 shrink-0">
                Vote
              </Badge>
            </div>
          ))}
        </div>
      </section>

      {/* Voting Dialog */}
      <Dialog open={!!activeGame} onOpenChange={(v) => { if (!v) setActiveGame(null); }}>
        <DialogContent
          className="p-0 w-[98vw] max-w-md max-h-[98dvh] overflow-y-auto rounded-xl border-0 shadow-2xl bg-background text-foreground [&>button]:hidden"
          data-testid="bf-voting-dialog"
        >
          <DialogHeader className="sr-only">
            <DialogTitle>B&F Voting</DialogTitle>
          </DialogHeader>
          <div className="relative">
            <button
              onClick={() => setActiveGame(null)}
              className="absolute top-3 right-3 z-10 h-8 w-8 rounded-full bg-background/80 backdrop-blur border border-border flex items-center justify-center hover:bg-background transition-colors"
              data-testid="bf-voting-close"
            >
              <X className="h-4 w-4" />
            </button>

            <div className="px-5 pt-5 pb-3">
              <div className="flex items-center gap-2 mb-1">
                <Trophy className="h-4 w-4 text-amber-500" />
                <h3 className="text-base font-bold">B&F Voting</h3>
              </div>
              <div className="text-xs text-muted-foreground">
                {activeGame?.grade || activeGame?.club_team_name} — {activeGame?.round}
              </div>
              <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
                <span>Tap a vote badge, then tap a player to assign it.</span>
              </div>

              {/* Available vote chips */}
              <div className="flex items-center gap-2 mt-3">
                {VOTE_VALUES.map((v) => (
                  <div
                    key={v}
                    className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                      usedValues.has(v) ? "bg-muted text-muted-foreground/40 line-through" : VOTE_COLORS[v]
                    }`}
                  >
                    {v}
                  </div>
                ))}
                <span className="text-[11px] text-muted-foreground ml-1">
                  {availableValues.length === 0 ? "All votes assigned" : `${availableValues.length} remaining`}
                </span>
              </div>
            </div>

            <ScrollArea className="max-h-[60dvh] overflow-y-auto">
              <div className="px-5 pb-5 space-y-1.5">
                {playersLoading ? (
                  <div className="text-center text-sm text-muted-foreground py-8">Loading players…</div>
                ) : (
                  players.map((p) => {
                    const currentVote = votes[p.user_id];
                    const nextAvailable = availableValues[0];
                    return (
                      <div
                        key={p.user_id}
                        className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border transition-all cursor-pointer ${
                          currentVote
                            ? "border-amber-500/40 bg-amber-500/5"
                            : "border-border hover:bg-muted/40"
                        }`}
                        onClick={() => {
                          if (currentVote) {
                            removeVote(p.user_id);
                          } else if (nextAvailable) {
                            assignVote(p.user_id, nextAvailable);
                          }
                        }}
                        data-testid={`bf-player-${p.user_id}`}
                      >
                        {p.avatar_url ? (
                          <img src={p.avatar_url} alt="" className="h-10 w-10 rounded-full object-cover shrink-0" />
                        ) : (
                          <div className="h-10 w-10 rounded-full bg-[hsl(var(--secondary))] flex items-center justify-center text-sm font-bold text-muted-foreground shrink-0">
                            {(p.first_name?.[0] || "").toUpperCase()}{(p.last_name?.[0] || "").toUpperCase()}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">{p.first_name} {p.last_name}</div>
                        </div>
                        {currentVote ? (
                          <div className={`h-8 w-8 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${VOTE_COLORS[currentVote]}`}>
                            {currentVote}
                          </div>
                        ) : (
                          <div className="h-8 w-8 rounded-full border-2 border-dashed border-border/60 flex items-center justify-center text-xs text-muted-foreground/50 shrink-0">
                            {nextAvailable || "—"}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </ScrollArea>

            <div className="px-5 pb-5 pt-2 border-t border-border/50">
              <Button
                className="w-full h-11"
                disabled={Object.keys(votes).length !== 3 || submitting}
                onClick={submitVotes}
                data-testid="bf-submit-votes"
              >
                {submitting ? "Saving…" : (
                  <>
                    <Check className="h-4 w-4 mr-1.5" />
                    Save B&F Votes
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
